(() => {
  'use strict';

  // This asset can be included by the homepage slideshow section and by the
  // global quiz loader. Guard against registering the same delegated events
  // twice when both script tags are present on the homepage.
  if (window.__KMPNGuidedSlideshowLoaded) return;
  window.__KMPNGuidedSlideshowLoaded = true;

  const OPEN_SELECTOR = '[data-kmpn-quiz-open]';
  const CLOSE_SELECTOR = '[data-kmpn-quiz-close]';
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Built-in quiz messages in the page language: Arabic on Arabic pages,
  // English on every other language. The language comes from <html lang>.
  const KMPN_LANG = String(document.documentElement.lang || '').toLowerCase().split('-')[0] === 'ar' ? 'ar' : 'en';
  const KMPN_STRINGS = {
    ar: {
      progress: 'سؤال {step} من 3',
      results: 'الترشيحات',
      bestMatch: 'الأفضل لك',
      goodMatch: 'مناسب لك',
      matchReason: 'اختيار مطابق لإجاباتك',
      adding: 'جاري الإضافة...',
      added: 'تمت الإضافة ✓',
      addFailed: 'تعذر الإضافة',
      cartError: 'تعذر إضافة المنتج للسلة.',
      noVariant: 'لم يتم العثور على متغير منتج صالح لإضافته للسلة.',
      invalidVariant: 'Variant غير صالح'
    },
    en: {
      progress: 'Question {step} of 3',
      results: 'Your recommendations',
      bestMatch: 'Best for you',
      goodMatch: 'A good fit',
      matchReason: 'Matches your answers',
      adding: 'Adding...',
      added: 'Added ✓',
      addFailed: "Couldn't add",
      cartError: "Couldn't add the product to your cart.",
      noVariant: 'No valid product option was found to add to your cart.',
      invalidVariant: 'Invalid product option'
    }
  };

  function kmpnText(key, values = {}) {
    const text = KMPN_STRINGS[KMPN_LANG][key] || KMPN_STRINGS.ar[key] || '';
    return text.replace(/\{(\w+)\}/g, (match, name) => (name in values ? String(values[name]) : match));
  }

  function dispatchThemeEvent(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  function announceCartError(error) {
    const errorMessage = error instanceof Error ? error.message : String(error || kmpnText('cartError'));
    dispatchThemeEvent('eurus:product:add-failed', { errorMessage });
  }

  function announceCartUpdate(payload) {
    const addedItems = Array.isArray(payload && payload.items) ? payload.items : [];

    // Eurus public events: notify integrations, then explicitly reload the
    // mini-cart/cart page because this add request happens outside the
    // theme's native product form flow.
    addedItems.forEach((product) => {
      dispatchThemeEvent('eurus:product:added', { product });
    });
    dispatchThemeEvent('eurus:cart:items-changed', { cart: payload });
    dispatchThemeEvent('eurus:cart:reload');

    // Compatibility fallbacks for apps or older custom theme code.
    dispatchThemeEvent('cart:updated', payload);
    dispatchThemeEvent('cart:refresh', payload);
    dispatchThemeEvent('cart-drawer:refresh', payload);
  }

  async function addItems(items) {
    const normalizedItems = (Array.isArray(items) ? items : [])
      .map((item) => ({
        id: String(item && item.id ? item.id : '').trim(),
        quantity: Math.max(1, Number(item && item.quantity ? item.quantity : 1))
      }))
      .filter((item) => item.id);

    if (!normalizedItems.length) {
      throw new Error(kmpnText('noVariant'));
    }

    let response;
    let payload;

    try {
      response = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: normalizedItems })
      });

      payload = await response.json();
    } catch (error) {
      announceCartError(error);
      throw error;
    }

    if (!response.ok) {
      const error = new Error(
        payload.description || payload.message || payload.status || kmpnText('cartError')
      );
      announceCartError(error);
      throw error;
    }

    announceCartUpdate(payload);
    return payload;
  }

  function getVisibleCards(modal) {
    return Array.from(modal.querySelectorAll('.kmpn-quiz-product:not([hidden])'));
  }


  function revealViewCart(modal, sourceButton = null) {
    modal.querySelectorAll('[data-kmpn-view-cart]').forEach((link) => {
      link.hidden = false;
    });

    const productCard = sourceButton && sourceButton.closest('.kmpn-quiz-product');
    const cardCartLink = productCard && productCard.querySelector('[data-kmpn-card-view-cart]');
    if (cardCartLink) cardCartLink.hidden = false;
  }

  function getSliderParts(modal) {
    const wrapper = modal.querySelector('[data-kmpn-product-slider]');
    if (!wrapper) return {};

    return {
      wrapper,
      track: wrapper.querySelector('[data-kmpn-product-grid]'),
      previousButton: wrapper.querySelector('[data-kmpn-slider-prev]'),
      nextButton: wrapper.querySelector('[data-kmpn-slider-next]')
    };
  }

  function getNormalizedScrollPosition(track) {
    const isRtl = window.getComputedStyle(track).direction === 'rtl';
    return isRtl ? Math.abs(track.scrollLeft) : track.scrollLeft;
  }

  function updateSliderButtons(modal) {
    const { wrapper, track, previousButton, nextButton } = getSliderParts(modal);
    if (!wrapper || !track) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const currentScroll = getNormalizedScrollPosition(track);
    const isScrollable = maxScroll > 8;

    wrapper.classList.toggle('is-scrollable', isScrollable);

    if (previousButton) {
      previousButton.hidden = !isScrollable;
      previousButton.disabled = !isScrollable || currentScroll <= 4;
    }

    if (nextButton) {
      nextButton.hidden = !isScrollable;
      nextButton.disabled = !isScrollable || currentScroll >= maxScroll - 4;
    }
  }

  function updateProductSlider(modal, resetPosition = false) {
    const { track } = getSliderParts(modal);
    if (!track) return;

    if (resetPosition) track.scrollLeft = 0;

    window.requestAnimationFrame(() => {
      updateSliderButtons(modal);
      window.setTimeout(() => updateSliderButtons(modal), 80);
    });
  }

  function scrollProductSlider(modal, direction) {
    const { track } = getSliderParts(modal);
    if (!track) return;

    const isRtl = window.getComputedStyle(track).direction === 'rtl';
    const distance = Math.max(240, track.clientWidth * 0.88);
    const logicalDirection = direction === 'next' ? 1 : -1;
    const left = logicalDirection * distance * (isRtl ? -1 : 1);

    track.scrollBy({ left, behavior: 'smooth' });
    window.setTimeout(() => updateSliderButtons(modal), 360);
  }

  function initialiseProductSlider(modal) {
    const { track, previousButton, nextButton } = getSliderParts(modal);
    if (!track) return;

    previousButton?.addEventListener('click', () => scrollProductSlider(modal, 'previous'));
    nextButton?.addEventListener('click', () => scrollProductSlider(modal, 'next'));
    track.addEventListener('scroll', () => updateSliderButtons(modal), { passive: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => updateSliderButtons(modal));
      resizeObserver.observe(track);
    }
  }

  function parseTokens(value) {
    return String(value || '')
      .toLowerCase()
      .split('||')
      .map((token) => token.trim())
      .filter(Boolean);
  }

  function mergeTokens(...values) {
    return Array.from(new Set(values.flatMap(parseTokens))).join('||');
  }

  function deduplicateProductCards(modal) {
    const uniqueCards = new Map();

    modal.querySelectorAll('.kmpn-quiz-product').forEach((card) => {
      const key = card.dataset.productHandle || card.dataset.variantId;
      if (!key) return;

      const existing = uniqueCards.get(key);
      if (!existing) {
        uniqueCards.set(key, card);
        return;
      }

      existing.dataset.collections = mergeTokens(
        existing.dataset.collections,
        card.dataset.collections
      );
      existing.dataset.tags = mergeTokens(existing.dataset.tags, card.dataset.tags);
      existing.dataset.ruleKeys = mergeTokens(existing.dataset.ruleKeys, card.dataset.ruleKeys);
      card.remove();
    });
  }

  function updateSelections(modal, answers) {
    modal.querySelectorAll('[data-kmpn-selection-list]').forEach((list) => {
      list.innerHTML = '';
      answers.filter(Boolean).forEach((answer) => {
        const pill = document.createElement('span');
        pill.className = 'kmpn-quiz__selection-pill';
        pill.textContent = answer.label;
        list.appendChild(pill);
      });
    });
  }

  function showStep(modal, step, answers) {
    modal.querySelectorAll('[data-kmpn-step]').forEach((panel) => {
      panel.hidden = Number(panel.dataset.kmpnStep) !== step;
    });

    const label = modal.querySelector('[data-kmpn-progress-label]');
    if (label) {
      label.textContent = step <= 3 ? kmpnText('progress', { step }) : kmpnText('results');
    }

    modal.querySelectorAll('[data-kmpn-progress-segment]').forEach((segment, index) => {
      segment.classList.toggle('is-active', step <= 3 && index === step - 1);
      segment.classList.toggle('is-complete', step > index + 1 || step === 4);
    });

    updateSelections(modal, answers);

    const body = modal.querySelector('.kmpn-quiz__body');
    if (body) body.scrollTop = 0;

    const activePanel = modal.querySelector(`[data-kmpn-step="${step}"]`);
    const firstAction = activePanel && activePanel.querySelector('button, a[href]');
    window.setTimeout(() => firstAction && firstAction.focus({ preventScroll: true }), 60);

    if (step === 4) {
      window.setTimeout(() => updateProductSlider(modal, true), 80);
    }
  }

  function ruleConditionMatches(condition, answerValue) {
    const choice = String(condition || '*').toLowerCase();
    const value = String(answerValue || '').toLowerCase();

    // “*” means this question is not required by the rule. This lets the
    // five flexible rules resolve after one or two answers instead of always
    // waiting for all three questions.
    if (choice === '*') return true;

    // A leading “!” means any answered value except the supplied token.
    // Example: !q1-honey matches every Question 1 option except natural honey.
    if (choice.startsWith('!')) {
      return Boolean(value) && value !== choice.slice(1);
    }

    return Boolean(value) && choice === value;
  }

  function ruleSpecificity(condition) {
    const choice = String(condition || '*').toLowerCase();
    if (choice === '*') return 0;
    if (choice.startsWith('!')) return 1;
    return 2;
  }

  function findBestResultRule(modal, answers, options = {}) {
    const values = answers.map((answer) => String((answer && answer.value) || '').toLowerCase());
    const flexibleOnly = Boolean(options.flexibleOnly);

    return Array.from(modal.querySelectorAll('[data-kmpn-result-rule]'))
      .filter((element) => !flexibleOnly || element.dataset.flexibleRule === 'true')
      .map((element, index) => {
        const choices = [element.dataset.q1, element.dataset.q2, element.dataset.q3]
          .map((value) => String(value || '*').toLowerCase());
        const matches = choices.every((choice, choiceIndex) => {
          return ruleConditionMatches(choice, values[choiceIndex]);
        });
        const specificity = choices.reduce(
          (total, choice) => total + ruleSpecificity(choice),
          0
        );

        return {
          element,
          index,
          key: String(element.dataset.ruleKey || '').toLowerCase(),
          matches,
          specificity
        };
      })
      .filter((rule) => rule.matches && rule.key)
      .sort((a, b) => b.specificity - a.specificity || a.index - b.index)[0] || null;
  }

  function showProductsForAnswers(modal, answers, matchedRuleOverride = null) {
    deduplicateProductCards(modal);

    const cards = Array.from(modal.querySelectorAll('.kmpn-quiz-product'));
    const limit = Math.max(4, Number(modal.dataset.resultProductLimit || 8));
    const matchedRule = matchedRuleOverride || findBestResultRule(modal, answers);
    let visibleIndex = 0;

    cards.forEach((card) => {
      const ruleKeys = parseTokens(card.dataset.ruleKeys);
      const shouldShow = Boolean(
        matchedRule &&
        ruleKeys.includes(matchedRule.key) &&
        visibleIndex < limit
      );

      card.hidden = !shouldShow;
      if (!shouldShow) return;

      visibleIndex += 1;
      card.dataset.rank = String(visibleIndex);

      const badge = card.querySelector('[data-kmpn-recommendation-badge]');
      if (badge) badge.textContent = visibleIndex === 1 ? kmpnText('bestMatch') : kmpnText('goodMatch');

      const reason = card.querySelector('.kmpn-quiz-product__reason');
      if (reason) reason.textContent = kmpnText('matchReason');
    });

    const noResult = modal.querySelector('[data-kmpn-no-result]');
    if (noResult) noResult.hidden = visibleIndex > 0;

    const bundle = modal.querySelector('[data-kmpn-bundle]');
    if (bundle) bundle.hidden = visibleIndex === 0;

    const bundleButton = modal.querySelector('[data-kmpn-add-all]');
    if (bundleButton) bundleButton.hidden = visibleIndex === 0;

    updateProductSlider(modal, true);

    // Product images and the popup opening animation can change the real
    // track width after the initial result render. Recheck after both have
    // settled so the arrows are enabled whenever more cards exist off-screen.
    window.setTimeout(() => updateProductSlider(modal, false), 240);
    window.setTimeout(() => updateProductSlider(modal, false), 650);
  }

  function setButtonState(button, state, message) {
    if (!button) return;
    if (!button.dataset.originalLabel) button.dataset.originalLabel = button.textContent.trim();

    if (state === 'loading') {
      button.disabled = true;
      button.textContent = kmpnText('adding');
    } else if (state === 'success') {
      button.disabled = true;
      button.textContent = kmpnText('added');
      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = button.dataset.originalLabel;
      }, 1800);
    } else if (state === 'error') {
      button.disabled = false;
      button.textContent = message || kmpnText('addFailed');
      window.setTimeout(() => {
        button.textContent = button.dataset.originalLabel;
      }, 2600);
    } else {
      button.disabled = false;
      button.textContent = button.dataset.originalLabel;
    }
  }

  function initialiseModal(modal, opener) {
    const answers = [null, null, null];
    const branchingQ1Value = String(modal.dataset.branchingQ1Value || 'q1-honey').toLowerCase();
    let currentStep = 1;
    let lastFocused = opener;

    initialiseProductSlider(modal);

    const clearQuestion = (question) => {
      if (!question || question < 1 || question > 3) return;
      answers[question - 1] = null;
      modal.querySelectorAll(`[data-question="${question}"]`).forEach((option) => {
        option.classList.remove('is-selected');
        option.setAttribute('aria-pressed', 'false');
      });
    };

    const close = () => {
      modal.classList.remove('is-open');
      document.body.classList.remove('kmpn-quiz-open');
      window.setTimeout(() => {
        modal.remove();
        if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      }, 220);
    };

    modal.querySelectorAll(CLOSE_SELECTOR).forEach((button) => button.addEventListener('click', close));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });

    modal.querySelectorAll('[data-kmpn-answer]').forEach((button) => {
      button.addEventListener('click', () => {
        const question = Number(button.dataset.question);
        if (!question || question < 1 || question > 3) return;

        modal.querySelectorAll(`[data-question="${question}"]`).forEach((option) => {
          option.classList.toggle('is-selected', option === button);
          option.setAttribute('aria-pressed', option === button ? 'true' : 'false');
        });

        answers[question - 1] = {
          value: button.dataset.value || '',
          label: button.dataset.label || button.textContent.trim(),
        };

        updateSelections(modal, answers);
        window.setTimeout(() => {
          const showMatchedResults = () => {
            // Early completion is intentionally limited to flexible rules
            // 21–30. Rules 1–20 keep their original final-result behaviour.
            const matchedRule = findBestResultRule(modal, answers, { flexibleOnly: true });
            if (!matchedRule) return false;
            showProductsForAnswers(modal, answers, matchedRule);
            currentStep = 4;
            showStep(modal, currentStep, answers);
            return true;
          };

          if (question === 1) {
            clearQuestion(2);
            clearQuestion(3);

            // A one-answer flexible rule can finish immediately after Q1.
            // This also supports “any option except natural honey”.
            if (showMatchedResults()) return;

            if (String(answers[0].value || '').toLowerCase() === branchingQ1Value) {
              currentStep = 2;
              showStep(modal, currentStep, answers);
            } else {
              // Non-honey choices still go directly to the result screen. If
              // no rule was configured, the normal no-result message appears.
              showProductsForAnswers(modal, answers);
              currentStep = 4;
              showStep(modal, currentStep, answers);
            }
            return;
          }

          if (question === 2) {
            clearQuestion(3);

            // A two-answer rule such as natural honey + gift can finish here
            // without displaying Question 3.
            if (showMatchedResults()) return;

            currentStep = 3;
            showStep(modal, currentStep, answers);
            return;
          }

          showProductsForAnswers(modal, answers);
          currentStep = 4;
          showStep(modal, currentStep, answers);
        }, 180);
      });
    });

    modal.querySelectorAll('[data-kmpn-back]').forEach((button) => {
      button.addEventListener('click', () => {
        if (currentStep === 3) {
          clearQuestion(3);
          currentStep = 2;
        } else if (currentStep === 2) {
          clearQuestion(2);
          clearQuestion(3);
          currentStep = 1;
        } else {
          return;
        }

        showStep(modal, currentStep, answers);
      });
    });

    modal.querySelectorAll('[data-kmpn-restart]').forEach((button) => {
      button.addEventListener('click', () => {
        answers.fill(null);
        modal.querySelectorAll('[data-kmpn-answer]').forEach((option) => {
          option.classList.remove('is-selected');
          option.setAttribute('aria-pressed', 'false');
        });
        currentStep = 1;
        showStep(modal, currentStep, answers);
      });
    });

    modal.addEventListener('click', async (event) => {
      const addButton = event.target.closest('[data-kmpn-add-product]');
      if (!addButton) return;

      const variantId = String(addButton.dataset.variantId || '').trim();
      if (!variantId) {
        setButtonState(addButton, 'error', kmpnText('invalidVariant'));
        return;
      }

      try {
        setButtonState(addButton, 'loading');
        await addItems([{ id: variantId, quantity: 1 }]);
        setButtonState(addButton, 'success');
        revealViewCart(modal, addButton);
      } catch (error) {
        console.error('[KMPN quiz] Add to cart failed:', error);
        setButtonState(addButton, 'error', error.message);
      }
    });

    const addAllButton = modal.querySelector('[data-kmpn-add-all]');
    if (addAllButton) {
      addAllButton.addEventListener('click', async () => {
        const items = getVisibleCards(modal)
          .map((card) => String(card.dataset.variantId || '').trim())
          .filter(Boolean)
          .map((id) => ({ id, quantity: 1 }));

        if (!items.length) return;

        try {
          setButtonState(addAllButton, 'loading');
          await addItems(items);
          setButtonState(addAllButton, 'success');
          revealViewCart(modal);
        } catch (error) {
          console.error('[KMPN quiz] Add all to cart failed:', error);
          setButtonState(addAllButton, 'error', error.message);
        }
      });
    }

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(modal.querySelectorAll(FOCUSABLE)).filter((element) => {
        return !element.hidden && element.offsetParent !== null;
      });
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.body.classList.add('kmpn-quiz-open');
    requestAnimationFrame(() => modal.classList.add('is-open'));
    showStep(modal, 1, answers);
  }

  function openFromTemplate(template, opener) {
    if (!(template instanceof HTMLTemplateElement)) return false;

    const existingModal = document.querySelector('.kmpn-quiz-modal.is-open');
    if (existingModal) return true;

    const modal = template.content.firstElementChild.cloneNode(true);
    document.body.appendChild(modal);
    initialiseModal(modal, opener);
    return true;
  }

  function openFromButton(button) {
    const templateId = button.dataset.templateId;
    const template = templateId ? document.getElementById(templateId) : null;
    return openFromTemplate(template, button);
  }

  function normalizeExternalTrigger(value) {
    return String(value || '')
      .trim()
      .replace(/^#/, '')
      .toLowerCase();
  }

  function findExternalQuizTemplate(trigger) {
    const wanted = normalizeExternalTrigger(trigger);
    if (!wanted) return null;

    return Array.from(document.querySelectorAll('template[data-kmpn-external-trigger]')).find((template) => {
      return normalizeExternalTrigger(template.dataset.kmpnExternalTrigger) === wanted;
    }) || null;
  }

  let remoteQuizTemplatesPromise = null;
  let remoteQuizTemplatesLoaded = false;

  function isStoreHomepage() {
    try {
      const rootUrl = new URL(routeRoot(), window.location.origin);
      const normalizePath = (path) => {
        const value = String(path || '/').replace(/\/+$/, '');
        return value || '/';
      };
      return normalizePath(window.location.pathname) === normalizePath(rootUrl.pathname);
    } catch (_) {
      return window.location.pathname === '/';
    }
  }

  async function prefetchExternalQuizTemplates() {
    // On the homepage, the real templates already exist in the hero section.
    if (isStoreHomepage()) {
      remoteQuizTemplatesLoaded = true;
      return;
    }

    if (remoteQuizTemplatesPromise) return remoteQuizTemplatesPromise;

    remoteQuizTemplatesPromise = (async () => {
      try {
        const response = await fetch(routeRoot(), {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) return;

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const remoteTemplates = Array.from(
          parsed.querySelectorAll('template[data-kmpn-external-trigger]')
        );

        remoteTemplates.forEach((remoteTemplate) => {
          const trigger = normalizeExternalTrigger(remoteTemplate.dataset.kmpnExternalTrigger);
          if (!trigger || findExternalQuizTemplate(trigger)) return;

          // Import only the template source. The modal itself is cloned into
          // document.body only when the CTA is clicked, exactly like it is on
          // the homepage. This keeps the homepage Guided slide as the single
          // source of truth for questions, colors, rules and collections.
          const imported = document.importNode(remoteTemplate, true);
          imported.removeAttribute('id');
          imported.dataset.kmpnRemoteTemplate = 'true';
          document.body.appendChild(imported);
        });
      } catch (error) {
        console.warn('[KMPN quiz] Could not load homepage quiz template.', error);
      } finally {
        remoteQuizTemplatesLoaded = true;
      }
    })();

    return remoteQuizTemplatesPromise;
  }

  function getLinkHashTrigger(link) {
    if (!(link instanceof HTMLAnchorElement)) return '';

    try {
      const url = new URL(link.getAttribute('href') || '', window.location.href);
      if (!url.hash) return '';

      // Only hijack same-page/same-store links. Normal links with a hash keep
      // their native behaviour unless their hash matches a configured quiz.
      if (url.origin !== window.location.origin) return '';
      return normalizeExternalTrigger(decodeURIComponent(url.hash.slice(1)));
    } catch (_) {
      return '';
    }
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest(OPEN_SELECTOR);
    if (button) {
      event.preventDefault();
      openFromButton(button);
      return;
    }

    const link = event.target.closest('a[href]');
    if (!link) return;

    const trigger = getLinkHashTrigger(link);
    if (!trigger) return;

    let template = findExternalQuizTemplate(trigger);
    if (template) {
      event.preventDefault();
      openFromTemplate(template, link);
      return;
    }

    // Inner pages do not contain the homepage hero section, so the quiz
    // template is not in their HTML. While the homepage template is loading,
    // hold the hash click, then retry with the imported template.
    if (!remoteQuizTemplatesLoaded) {
      event.preventDefault();
      await prefetchExternalQuizTemplates();
      template = findExternalQuizTemplate(trigger);
      if (template) {
        openFromTemplate(template, link);
      } else {
        // It was a normal in-page hash, not a quiz trigger. Preserve the
        // browser's expected anchor behaviour.
        const href = link.getAttribute('href');
        if (href) window.location.href = href;
      }
    }
  });

  // Optional public hook for theme/app code that wants to open the same popup
  // without relying on a link. Example:
  // document.dispatchEvent(new CustomEvent('kmpn:quiz:open', { detail: { trigger: 'help-me-choose' } }));
  document.addEventListener('kmpn:quiz:open', async (event) => {
    const trigger = event && event.detail ? event.detail.trigger : '';
    let template = findExternalQuizTemplate(trigger);
    if (!template) {
      await prefetchExternalQuizTemplates();
      template = findExternalQuizTemplate(trigger);
    }
    if (template) openFromTemplate(template, document.activeElement);
  });

  // Preload homepage-configured quiz templates on internal pages so the
  // header CTA opens instantly and uses exactly the same hero settings.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prefetchExternalQuizTemplates, { once: true });
  } else {
    prefetchExternalQuizTemplates();
  }
  const KMPN_MOBILE_QUERY = window.matchMedia('(max-width: 767px)');

  function initialiseGuidedSlideAutoHeight(root) {
    if (!root || root.dataset.kmpnAutoHeightReady === 'true') return;

    const track = root.querySelector('.kmpn-slideshow-frame');
    const list = root.querySelector('.kmpn-slideshow-list');
    if (!track || !list) return;

    root.dataset.kmpnAutoHeightReady = 'true';

    let animationFrame = 0;
    let lastHeight = 0;
    let nativeMobileHeight = 0;

    const rememberNativeHeight = () => {
      if (!KMPN_MOBILE_QUERY.matches || nativeMobileHeight > 0) return;
      const current = Math.ceil(track.getBoundingClientRect().height);
      if (Number.isFinite(current) && current > 0) nativeMobileHeight = current;
    };

    const clearMeasuredHeight = () => {
      lastHeight = 0;
      root.classList.remove('kmpn-mobile-guided-active');
      track.style.removeProperty('height');
      list.style.removeProperty('height');
      root.querySelectorAll('.kmpn-guided-slide').forEach((slide) => {
        slide.style.removeProperty('height');
      });
    };

    const measureTallestGuidedSlide = () => {
      let tallest = 0;

      root.querySelectorAll('.kmpn-guided-slide__inner').forEach((inner) => {
        const slide = inner.closest('.kmpn-guided-slide');
        if (!slide) return;

        /*
          Measure the REAL rendered bottom edge of the mobile content.

          scrollHeight alone can under-report inside Eurus/Splide because the
          track and slide already have a fixed mobile height. Cloning also
          proved unreliable for this section because some theme/layout rules
          depend on the live slideshow tree.

          Instead we temporarily force content-visibility on, then measure the
          lowest direct child (content/media) relative to the inner wrapper.
          This still works when that child extends beyond the current Splide
          height, so the new track height cannot crop the bottom of the slide.
        */
        const previousContentVisibility = slide.style.contentVisibility;
        slide.style.contentVisibility = 'visible';

        const innerRect = inner.getBoundingClientRect();
        const computed = window.getComputedStyle(inner);
        const paddingBottom = parseFloat(computed.paddingBottom) || 0;
        let contentBottom = 0;

        Array.from(inner.children).forEach((child) => {
          const rect = child.getBoundingClientRect();
          if (!rect || !Number.isFinite(rect.bottom)) return;
          contentBottom = Math.max(contentBottom, rect.bottom - innerRect.top);
        });

        const renderedHeight = contentBottom > 0
          ? contentBottom + paddingBottom
          : 0;

        const requiredHeight = Math.ceil(Math.max(
          inner.scrollHeight || 0,
          inner.getBoundingClientRect().height || 0,
          renderedHeight
        ));

        if (previousContentVisibility) {
          slide.style.contentVisibility = previousContentVisibility;
        } else {
          slide.style.removeProperty('content-visibility');
        }

        if (Number.isFinite(requiredHeight) && requiredHeight > tallest) {
          tallest = requiredHeight;
        }
      });

      // Only a tiny allowance is needed for sub-pixel rounding. A large
      // allowance becomes visible as empty space below square media.
      return tallest > 0 ? tallest + 6 : 0;
    };

    const updateHeight = () => {
      animationFrame = 0;

      if (!KMPN_MOBILE_QUERY.matches) {
        clearMeasuredHeight();
        nativeMobileHeight = 0;
        return;
      }

      rememberNativeHeight();

      const tallestGuidedHeight = measureTallestGuidedSlide();

      /*
        When guided slides exist, their real content height is the source of
        truth. Do NOT keep the native Eurus configured mobile height as a
        minimum: values such as 750/900px create the large empty area visible
        below otherwise-complete square media. Fall back to the native height
        only if measurement is not available yet.
      */
      const uniformHeight = Math.ceil(
        tallestGuidedHeight > 0 ? tallestGuidedHeight : nativeMobileHeight
      );

      if (!Number.isFinite(uniformHeight) || uniformHeight < 1) return;
      if (uniformHeight === lastHeight && root.classList.contains('kmpn-mobile-guided-active')) return;

      lastHeight = uniformHeight;
      root.classList.add('kmpn-mobile-guided-active');

      /*
        Keep ONE mobile height for the whole slideshow. This prevents the page
        from jumping when a slide has a longer heading, extra benefit row, or
        slightly different content. Normal Eurus slides also fill this height.
      */
      track.style.setProperty('height', `${uniformHeight}px`, 'important');
      list.style.setProperty('height', `${uniformHeight}px`, 'important');
      root.querySelectorAll('.kmpn-guided-slide').forEach((slide) => {
        slide.style.setProperty('height', `${uniformHeight}px`, 'important');
      });
    };

    const scheduleUpdate = () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(updateHeight);
    };

    const mutationObserver = new MutationObserver((mutations) => {
      const needsUpdate = mutations.some((mutation) => {
        return (
          mutation.type === 'childList' ||
          (mutation.type === 'attributes' && mutation.attributeName === 'class')
        );
      });
      if (needsUpdate) scheduleUpdate();
    });

    mutationObserver.observe(list, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(scheduleUpdate);
      root.querySelectorAll('.kmpn-guided-slide__inner').forEach((inner) => {
        resizeObserver.observe(inner);
      });
    }

    root.addEventListener('load', scheduleUpdate, true);
    root.addEventListener('loadedmetadata', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    KMPN_MOBILE_QUERY.addEventListener?.('change', scheduleUpdate);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleUpdate).catch(() => {});
    }

    rememberNativeHeight();
    scheduleUpdate();
    window.setTimeout(scheduleUpdate, 120);
    window.setTimeout(scheduleUpdate, 450);
    window.setTimeout(scheduleUpdate, 1000);
  }

  function initialiseAllGuidedSlideHeights(scope = document) {
    const roots = [];
    if (scope.matches && scope.matches('.kmpn-has-guided-slide')) roots.push(scope);
    if (scope.querySelectorAll) {
      roots.push(...scope.querySelectorAll('.kmpn-has-guided-slide'));
    }
    roots.forEach(initialiseGuidedSlideAutoHeight);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAllGuidedSlideHeights());
  } else {
    initialiseAllGuidedSlideHeights();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAllGuidedSlideHeights(event.target);
  });

})();
